import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Cpu, HardDrive, MemoryStick, Pencil } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { prebuiltSystems } from "@/data/prebuiltSystems";
import { generatePCSlug } from "@/utils/seoUtils";

const FeaturedProducts = () => {
  const navigate = useNavigate();

  // Use actual prebuilt systems data for featured products
  const featuredProducts = prebuiltSystems
    .filter(pc => pc.bestseller || pc.featured) // Show bestsellers and featured products
    .slice(0, 3); // Limit to 3 products

  const handleBuyNow = (productName: string) => {
    const slug = generatePCSlug(productName);
    navigate(`/gaming-pcs/${slug}`);
  };

  return (
    <section className="py-16 bg-gaming-darker">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center mb-12">
          <h2 className="text-3xl font-bold text-white">Featured Systems</h2>
          <Link to="/gaming-pcs" className="text-gaming-blue hover:underline">View all systems</Link>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {featuredProducts.map((product) => (
            <Card key={product.id} className="gaming-card border border-gaming-light-gray/40 bg-gaming-dark h-full flex flex-col">
              <CardHeader className="p-0">
                <div className="relative h-56 w-full overflow-hidden">
                  <img 
                    src={product.imageUrl || "https://images.unsplash.com/photo-1531297484001-80022131f5a1?q=80&w=2000&auto=format"} 
                    alt={product.name} 
                    loading="lazy" className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                  {product.bestseller && (
                    <Badge className="absolute top-3 left-3 bg-gaming-red text-white font-medium">Best Seller</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="py-6 flex-grow">
                <div className="mb-3 flex justify-between items-center">
                  <Badge variant="outline" className="bg-gaming-light-gray/20 text-gray-300 hover:bg-gaming-light-gray/30">
                    {product.category}
                  </Badge>
                  <span className="text-gaming-blue font-bold">${product.price}</span>
                </div>
                <h3 className="text-xl font-bold mb-3 text-white">{product.name}</h3>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-gray-400 text-sm">
                    <Cpu size={14} />
                    <span>{product.cpuDisplay}</span>
                  </li>
                  <li className="flex items-center gap-2 text-gray-400 text-sm">
                    <HardDrive size={14} />
                    <span>{product.gpuDisplay}</span>
                  </li>
                  <li className="flex items-center gap-2 text-gray-400 text-sm">
                    <MemoryStick size={14} />
                    <span>{product.ramDisplay}</span>
                  </li>
                </ul>
              </CardContent>
              <CardFooter className="pt-0 pb-6 flex gap-3">
                <Button 
                  className="flex-1 bg-gaming-blue hover:bg-gaming-blue/80 text-white"
                  onClick={() => handleBuyNow(product.name)}
                >
                  Buy Now
                </Button>
                <Button 
                  variant="outline" 
                  className="flex-1 border-gaming-blue/50 hover:border-gaming-blue hover:bg-gaming-blue/10"
                  asChild
                >
                  <Link to="/customize">
                    <Pencil className="h-4 w-4 mr-1" />
                    Customize
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedProducts;
